/**
 * Free-text notes, at SECTION or QUESTION level (spec §2.4, brief §5).
 *
 * Notes are how the out-of-box situations reach the office: "non-elevator
 * equipment to move — the tenant's server rack, goes to the back wall" (G8),
 * the chilled water routing (H4), what the crane has to reach over (A1). The
 * catalog cannot enumerate those, so the note field is available on every
 * question and every section.
 *
 * ── One note per target, edited in place ─────────────────────────────────────
 * A section (or a question within it) has exactly ONE note. The row is found by
 * the RELATIONSHIP it carries — `sectionInstanceId`, and `questionId` where
 * there is one — because that is what a note IS; the id is only how the store
 * addresses it. `noteIdFor` derives an id for a note this app is minting for the
 * first time, and nothing else: notes that arrive from the office, from another
 * device or from a fixture carry ids of their own, and a field that looked its
 * row up by the derived id rendered EMPTY over them and then forked a second
 * row on the first keystroke. Editing reuses the row's own id, so `addNote`
 * upserts and typing edits one row instead of appending a hundred. `createdAt`
 * is the first write and is preserved on every edit after it.
 *
 * ── Every keystroke persists ─────────────────────────────────────────────────
 * No debounce and no "Save" button. A surveyor who force-quits mid-sentence
 * keeps the sentence. `store.addNote` commits to IndexedDB before it resolves,
 * so the textarea is a cache of the document exactly the way an answer is.
 */

import { useState, type ReactNode } from 'react';

import { shotNoteFor } from '../../core/validation';
import type { Note } from '../../core/types';
import { isoAt } from '../../db/store';
import { useServices } from '../state/services';
import { useSurvey } from '../state/SurveyProvider';
import { TextField } from './TextField';

/**
 * The id this app mints for a target's note, and the stable `data-note-id`
 * handle by which the target's field is found in the DOM. Deterministic, so a
 * note this app created is addressed the same way after a reload.
 *
 * It is a CONVENTION, not a guarantee: a note that reached the document any
 * other way carries the id it was born with — the shipped St Luke's fixture
 * numbers its notes `note-1` … `note-5` — so nothing may look a note UP by
 * this. Ownership is `sectionInstanceId` + `questionId`; see `NotesField` and
 * `QuestionWork`.
 */
export function noteIdFor(sectionInstanceId: string, questionId?: string): string {
  return questionId ? `note|${sectionInstanceId}|${questionId}` : `note|${sectionInstanceId}`;
}

/**
 * The note a target OWNS — this section, and this question within it, where an
 * absent `questionId` on both sides is the section's own note.
 *
 * Ownership is the relationship the note carries, never `noteIdFor`'s derived
 * id. The id is only how the store addresses a row; the relationship is what a
 * note IS, and a note that reached this document from the office, off another
 * device or out of a fixture carries an id this app never minted. Two callers
 * ask this question — the field that edits the note and `QuestionWork`, which
 * decides whether a question has any surface at all — and they answered it
 * separately once. They drifted, and every fixture note went dark.
 */
export function noteFor(
  notes: readonly Note[],
  sectionInstanceId: string,
  questionId?: string,
): Note | undefined {
  return notes.find(
    (note) => note.sectionInstanceId === sectionInstanceId && note.questionId === questionId,
  );
}

/**
 * The row one keystroke writes.
 *
 * Two rules, and both of them are about not losing what is already written.
 * The row keeps ITS OWN id whenever it has one — `noteIdFor` mints an id only
 * for a note being written here for the first time — so `store.addNote`'s
 * upsert edits the existing row instead of forking a second one beside it and
 * orphaning the surveyor's original text. And `createdAt` is the FIRST write,
 * preserved by every edit after it.
 */
export function noteEdit(
  existing: Note | undefined,
  sectionInstanceId: string,
  questionId: string | undefined,
  text: string,
  mintedAt: string,
): Note {
  return {
    id: existing?.id ?? noteIdFor(sectionInstanceId, questionId),
    sectionInstanceId,
    ...(questionId ? { questionId } : {}),
    text,
    createdAt: existing?.createdAt ?? mintedAt,
  };
}

export interface NotesFieldProps {
  sectionInstanceId: string;
  /** Absent = the section's note. */
  questionId?: string;
  label?: string;
  placeholder?: string;
  rows?: number;
}

export function NotesField({
  sectionInstanceId,
  questionId,
  label = questionId ? 'Note on this question' : 'Notes for this section',
  placeholder = 'Anything the office needs to know — what you saw, where it is, why it is odd.',
  rows = 3,
}: NotesFieldProps): ReactNode {
  const { store } = useServices();
  const { doc, addNote, readOnly } = useSurvey();
  const [error, setError] = useState<string | null>(null);

  // The row this field edits, found by RELATIONSHIP and never by the derived
  // id. Resolving it by `noteIdFor` showed an EMPTY field over a note that
  // exists — every note in the shipped St Luke's fixture, for one — and then
  // the first keystroke minted a SECOND row at the derived id, silently
  // orphaning what the surveyor had written. Since R15 the section's note is
  // the only way to say a section was blocked, so that row is not one to lose.
  const existing = noteFor(doc.notes, sectionInstanceId, questionId);

  const write = (text: string): void => {
    const note = noteEdit(existing, sectionInstanceId, questionId, text, isoAt(store.now()));
    addNote(note).catch((cause: unknown) => {
      setError(cause instanceof Error ? cause.message : 'Could not save that note.');
    });
  };

  return (
    <div className="notes">
      <TextField
        label={label}
        value={existing?.text ?? ''}
        onChange={write}
        placeholder={placeholder}
        multiline
        rows={rows}
        // Round 7 task 10, ruling 12: a submitted survey is read-only
        // everywhere, and `addNote` would be refused by the provider anyway
        // — this is the door disabled, not merely a refusal logged silently.
        disabled={readOnly}
        // The DERIVED id, deliberately, even when the row being edited carries
        // another: `data-note-id` names the TARGET, not the row. It is the
        // handle tests/e2e/reckoning.spec.ts reads the section id back out of.
        // A row id that arrived from elsewhere is not derivable by it. (Before
        // round 7 task 4, `PhotoViewer.goToNote` also rebuilt this id from a
        // photo's own section and question; the viewer's ✎ edits the photo's
        // own label now, and no longer touches notes at all.)
        dataId={noteIdFor(sectionInstanceId, questionId)}
      />
      {error ? (
        <p className="photos__error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/**
 * The id this app mints for a shot's own written reason (Amendment 8, D-041) —
 * the `shotListId` sibling of `noteIdFor`, for the one note kind that is owned
 * by a SHOT rather than a question. `shotNoteFor` (src/core/validation.ts) is
 * the ownership test; this is only the derived id for a first write.
 */
export function shotNoteIdFor(sectionInstanceId: string, shotListId: string): string {
  return `note|${sectionInstanceId}|shot|${shotListId}`;
}

/**
 * The row one keystroke writes on `ShotReasonField` — `noteEdit`'s own two
 * rules (keep the row's own id, keep its first `createdAt`), for a note keyed
 * by `shotListId` instead of `questionId`.
 */
export function shotNoteEdit(
  existing: Note | undefined,
  sectionInstanceId: string,
  shotListId: string,
  text: string,
  mintedAt: string,
): Note {
  return {
    id: existing?.id ?? shotNoteIdFor(sectionInstanceId, shotListId),
    sectionInstanceId,
    shotListId,
    text,
    createdAt: existing?.createdAt ?? mintedAt,
  };
}

/** One line: why a required photo was not taken (Amendment 8). Writes a shot note. */
export function ShotReasonField({
  sectionInstanceId,
  shotListId,
  shotLabel,
}: {
  sectionInstanceId: string;
  shotListId: string;
  shotLabel: string;
}): ReactNode {
  const { store } = useServices();
  const { doc, addNote, readOnly } = useSurvey();
  const existing = shotNoteFor(doc, sectionInstanceId, shotListId);
  const write = (text: string): void => {
    addNote(shotNoteEdit(existing, sectionInstanceId, shotListId, text.slice(0, 80), isoAt(store.now()))).catch(
      () => undefined,
    );
  };
  return (
    <TextField
      label={`Why not — ${shotLabel}`}
      value={existing?.text ?? ''}
      onChange={write}
      placeholder="Locked, no access, no such thing — one line."
      disabled={readOnly}
    />
  );
}
